import React, { useState, useEffect } from 'react';
import { FileRecord, fileApi, FileListResponse } from '../services/api';
import './DocumentUpload.css';

interface DocumentUploadProps {
  applicationId: string;
}

// Document categories based on loan application requirements
const DOCUMENT_CATEGORIES = [
  { id: 'loan_application', name: 'Loan Application', description: 'Completed and signed loan application form' },
  { id: 'proof_of_identity', name: 'Proof of Identity', description: 'Driver\'s License or Passport' },
  { id: 'proof_of_address', name: 'Proof of Address', description: 'Utility Bill (last 3 months)' },
  { id: 't4_forms', name: 'T4 Forms', description: 'Last two years of T4 forms' },
  { id: 'pay_stubs', name: 'Pay Stubs', description: 'Most recent 30 days of pay stubs' },
  { id: 'bank_statements', name: 'Bank Statements', description: 'Last two months of bank statements' },
  { id: 'tax_returns', name: 'Tax Returns', description: 'Last two years of federal tax returns' },
  { id: 'gift_letter', name: 'Gift Letter (if applicable)', description: 'Documentation for gifted funds for down payment' },
  { id: 'proof_of_funds', name: 'Proof of Funds', description: 'Evidence of sufficient funds for closing costs' }
];

const DocumentUpload: React.FC<DocumentUploadProps> = ({ applicationId }) => {
  const [files, setFiles] = useState<FileRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState<string | null>(null); // Track which category is uploading
  const [dragOver, setDragOver] = useState<string | null>(null); // Track drag over state

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadFiles();
  }, [applicationId]);

  const loadFiles = async () => {
    try {
      setLoading(true);
      const response: FileListResponse = await fileApi.getApplicationFiles(applicationId);
      setFiles(response.files);
    } catch (err) {
      setError('Failed to load files');
      console.error('Error loading files:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (categoryId: string, file: File) => {
    try {
      setUploading(categoryId);
      setError(null);

      const category = DOCUMENT_CATEGORIES.find(cat => cat.id === categoryId);
      const documentType = category?.name || 'Other';

      const newFile = await fileApi.uploadFileToApplication(applicationId, file, documentType);
      
      // Add the new file to the state directly instead of reloading
      setFiles(prevFiles => [...prevFiles, newFile]);
      
      // Reset file input
      const fileInput = document.getElementById(`file-input-${categoryId}`) as HTMLInputElement;
      if (fileInput) {
        fileInput.value = '';
      }

    } catch (err) {
      setError('Failed to upload file');
      console.error('Error uploading file:', err);
    } finally {
      setUploading(null);
    }
  };

  const handleMultipleFileUpload = async (categoryId: string, fileList: FileList) => {
    try {
      setUploading(categoryId);
      setError(null);

      const category = DOCUMENT_CATEGORIES.find(cat => cat.id === categoryId);
      const documentType = category?.name || 'Other';

      // Upload files sequentially and collect the uploaded file records
      const uploadedFiles: FileRecord[] = [];
      for (let i = 0; i < fileList.length; i++) {
        const newFile = await fileApi.uploadFileToApplication(applicationId, fileList[i], documentType);
        uploadedFiles.push(newFile);
      }
      
      // Add all new files to the state at once
      setFiles(prevFiles => [...prevFiles, ...uploadedFiles]);
      
      // Reset file input
      const fileInput = document.getElementById(`file-input-${categoryId}`) as HTMLInputElement;
      if (fileInput) {
        fileInput.value = '';
      }

    } catch (err) {
      setError('Failed to upload files');
      console.error('Error uploading files:', err);
    } finally {
      setUploading(null);
    }
  };

  const handleFileChange = (categoryId: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      if (files.length === 1) {
        handleFileUpload(categoryId, files[0]);
      } else {
        handleMultipleFileUpload(categoryId, files);
      }
    }
  };

  const handleDownload = async (file: FileRecord) => {
    try {
      const blob = await fileApi.downloadApplicationFile(applicationId, file.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = file.original_filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError('Failed to download file');
      console.error('Error downloading file:', err);
    }
  };

  const handleDelete = async (file: FileRecord) => {
    if (window.confirm(`Are you sure you want to delete "${file.original_filename}"?`)) {
      try {
        await fileApi.deleteApplicationFile(applicationId, file.id);
        // Remove the file from state directly instead of reloading
        setFiles(prevFiles => prevFiles.filter(f => f.id !== file.id));
      } catch (err) {
        setError('Failed to delete file');
        console.error('Error deleting file:', err);
      }
    }
  };

  const getFilesForCategory = (categoryId: string): FileRecord[] => {
    const category = DOCUMENT_CATEGORIES.find(cat => cat.id === categoryId);
    if (!category) return [];
    
    return files.filter(file => file.document_type === category.name);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getTotalFilesCount = (): number => {
    return files.length;
  };

  const getTotalSize = (): string => {
    const totalBytes = files.reduce((sum, file) => sum + file.file_size, 0);
    return formatFileSize(totalBytes);
  };

  const handleDragOver = (e: React.DragEvent, categoryId: string) => {
    e.preventDefault();
    setDragOver(categoryId);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(null);
  };

  const handleDrop = (e: React.DragEvent, categoryId: string) => {
    e.preventDefault();
    setDragOver(null);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      if (files.length === 1) {
        handleFileUpload(categoryId, files[0]);
      } else {
        handleMultipleFileUpload(categoryId, files);
      }
    }
  };

  if (loading) {
    return <div className="loading">Loading documents...</div>;
  }

  return (
    <div className="document-upload">
      <div className="documents-header">
        
        <div className="documents-summary" >
          <span className="file-count">{getTotalFilesCount()} files uploaded</span>
          <span className="total-size">Total size: {getTotalSize()}</span>
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="document-categories">
        {DOCUMENT_CATEGORIES.map(category => {
          const categoryFiles = getFilesForCategory(category.id);
          const isUploading = uploading === category.id;

          return (
            <div 
              key={category.id} 
              className={`document-category ${dragOver === category.id ? 'drag-over' : ''}`}
              onDragOver={(e) => handleDragOver(e, category.id)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, category.id)}
            >
              <div className="category-header">
                <div className="category-info">
                  <h4>{category.name}</h4>
                  <p className="category-description">{category.description}</p>
                  {dragOver === category.id && (
                    <p className="drag-hint">Drop files here to upload</p>
                  )}
                </div>
                <div className="category-actions">
                  <label 
                    htmlFor={`file-input-${category.id}`} 
                    className={`btn btn-upload ${isUploading ? 'uploading' : ''}`}
                  >
                    {isUploading ? 'Uploading...' : 'Choose Files'}
                  </label>
                  <input
                    id={`file-input-${category.id}`}
                    type="file"
                    multiple
                    onChange={(e) => handleFileChange(category.id, e)}
                    disabled={isUploading}
                    className="file-input-hidden"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.txt"
                  />
                  <span className="file-count-badge">{categoryFiles.length}</span>
                </div>
              </div>

              {categoryFiles.length > 0 && (
                <div className="category-files">
                  <table className="files-table">
                    <thead>
                      <tr>
                        <th>File Name</th>
                        <th>Size</th>
                        <th>Uploaded</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {categoryFiles.map(file => (
                        <tr key={file.id}>
                          <td className="file-name">{file.original_filename}</td>
                          <td className="file-size">{formatFileSize(file.file_size)}</td>
                          <td className="file-date">
                            {new Date(file.created_at).toLocaleDateString()}
                          </td>
                          <td className="file-actions">
                            <button
                              onClick={() => handleDownload(file)}
                              className="btn btn-sm btn-primary"
                              title="Download file"
                            >
                              ⬇
                            </button>
                            <button
                              onClick={() => handleDelete(file)}
                              className="btn btn-sm btn-danger"
                              title="Delete file"
                            >
                              🗑
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DocumentUpload;